import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, Sparkles } from 'lucide-react';
import { ArticleCard } from '../components/ArticleCard';
import { Button } from '../components/ui/button';
import { articlesAPI, categoriesAPI } from '../services/api';

export const Home = () => {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [articlesData, categoriesData] = await Promise.all([
        articlesAPI.getAll(),
        categoriesAPI.getAll()
      ]);
      
      setArticles(articlesData);
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const featuredArticles = articles.filter(article => article.featured);
  const mainFeatured = featuredArticles[0];
  const secondaryFeatured = featuredArticles.slice(1, 3);

  const getArticlesByCategory = (categorySlug) => {
    return articles.filter(article => article.category === categorySlug).slice(0, 3);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-teal-50 to-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 bg-teal-100 px-4 py-2 rounded-full mb-6">
              <Sparkles className="h-5 w-5 text-teal-600" />
              <span className="text-sm font-medium text-teal-900">
                Estrategias verificadas para generar ingresos
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4">
              Aprende a Ganar Dinero
              <span className="text-teal-600"> Online</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Guías prácticas, herramientas y consejos sobre apps, inteligencia artificial,
              negocios digitales y emprendimiento online.
            </p>
          </div>

          {/* Featured Articles Grid */}
          {featuredArticles.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Main Featured */}
              {mainFeatured && (
                <div className="lg:col-span-2">
                  <ArticleCard article={mainFeatured} featured={true} />
                </div>
              )}
              {/* Secondary Featured */}
              {secondaryFeatured.map((article) => (
                <div key={article.id}>
                  <ArticleCard article={article} featured={true} />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Explora por Categoría
            </h2>
            <p className="text-gray-600">
              Encuentra contenido específico según tus intereses
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {categories.map((category) => {
              const categoryArticles = getArticlesByCategory(category.slug);
              const articleCount = categoryArticles.length;
              
              return (
                <Link
                  key={category.id}
                  to={`/categoria/${category.slug}`}
                  className="group"
                >
                  <div className="bg-white rounded-xl p-6 border-2 border-gray-200 hover:border-teal-500 hover:shadow-lg transition-all duration-300">
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="text-lg font-bold text-gray-900 group-hover:text-teal-600 transition-colors">
                        {category.name}
                      </h3>
                      <TrendingUp className="h-5 w-5 text-teal-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <p className="text-sm text-gray-600 mb-4">{category.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">{articleCount} artículos</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-teal-600 hover:text-teal-700 group-hover:translate-x-1 transition-transform"
                      >
                        Ver más →
                      </Button>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Latest Articles by Category */}
      {categories.slice(0, 2).map((category) => {
        const categoryArticles = getArticlesByCategory(category.slug);
        
        if (categoryArticles.length === 0) return null;

        return (
          <section key={category.id} className="py-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">
                    {category.name}
                  </h2>
                  <p className="text-gray-600">{category.description}</p>
                </div>
                <Link to={`/categoria/${category.slug}`}>
                  <Button variant="outline" className="hover:bg-teal-50 hover:text-teal-600 hover:border-teal-500 transition-colors">
                    Ver todos
                  </Button>
                </Link>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {categoryArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            </div>
          </section>
        );
      })}

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-teal-500 to-teal-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            ¿Listo para empezar tu viaje financiero?
          </h2>
          <p className="text-teal-100 text-lg mb-8">
            Únete a miles de emprendedores que ya están generando ingresos online
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/categoria/apps">
              <Button size="lg" className="bg-white text-teal-600 hover:bg-gray-100 transition-colors">
                Explorar Apps
              </Button>
            </Link>
            <Link to="/categoria/consejos">
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-teal-600 transition-colors"
              >
                Ver Consejos
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
